import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { Shell, Side, Main } from '@leanopx/design-system';
import { useAuth } from '@/components/providers/auth-provider';
import { useUiStore } from '@/stores/ui-store';
import { useSidePin } from '@/hooks/use-side-pin';
import { CommandPalette } from './command-palette';
import { QuickCaptureProvider, useQuickCapture } from './quick-capture';
import { RailContentProvider } from './rail-content';
import { SidebarNav } from './sidebar-nav';
import { PortalTopbar } from './portal-topbar';
import { PortalRail, RailReopener } from './portal-rail';
import { ShortcutsCheatsheet } from './shortcuts-cheatsheet';
import { t } from '@/lib/i18n';

/**
 * AppShell — the three-column portal grid.
 *
 *   ┌────────┬──────────────────────────┬────────┐
 *   │        │  topbar (56px)           │        │
 *   │  side  ├──────────────────────────┤  rail  │
 *   │        │  work-main (scrolls)     │        │
 *   └────────┴──────────────────────────┴────────┘
 *
 * Sidebar lives in column 1, rows 1–2 (it absorbs both the topbar and
 * work rows so the org chip can sit above the topbar's leading edge).
 * Topbar spans columns 2–3 of row 1; main lives in column 2 row 2;
 * rail in column 3 row 2.
 *
 * Sidebar pin is bound to ⌘\ via `useSidePin`. Rail width comes from
 * the UI store, written by `useResizableRail`. On routes that don't
 * want a rail (`/alerts`, `/agent`) the shell switches to a 2-column
 * grid via the `.no-rail` modifier.
 */

const NO_RAIL_PATHS = new Set<string>(['/alerts', '/agent']);

export function AppShell() {
  return (
    <>
      <AppShellInner />
      <AppToaster />
    </>
  );
}

function AppToaster() {
  const theme = useUiStore((s) => s.theme);
  const sonnerTheme: 'light' | 'dark' | 'system' =
    theme === 'midnight' ? 'dark' : theme === 'system' ? 'system' : 'light';
  return (
    <Toaster
      position="bottom-right"
      theme={sonnerTheme}
      richColors
      closeButton
      toastOptions={{
        classNames: {
          toast:
            'group toast border-[var(--color-line)] bg-[var(--color-surface-2)] text-[var(--color-ink)] font-sans text-sm shadow-lg',
          description: 'text-xs text-[var(--color-ink-muted)]',
          actionButton:
            'bg-[var(--color-accent)] text-[var(--color-canvas)] hover:bg-[var(--color-accent-bright)]',
        },
      }}
    />
  );
}

function AppShellInner() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const railOpen = useUiStore((s) => s.railOpen);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { pinned } = useSidePin();
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const [sideRevealed, setSideRevealed] = useState(false);

  const railSuppressed = NO_RAIL_PATHS.has(path);
  const railVisible = !railSuppressed && railOpen;
  const railCollapsedReopener = !railSuppressed && !railOpen;

  // Close the narrow-viewport sidebar drawer when the route changes —
  // tapping a nav item should collapse the drawer rather than leave it
  // open over the new page.
  useEffect(() => {
    setSideRevealed(false);
  }, [path]);

  // Global `?` shortcut → toggle cheatsheet. Gate on logged-in state
  // (the early `!user` return below means we never mount this branch
  // unauthenticated, but we also re-check `user` inside the listener
  // to avoid any future reorder where the listener mounts before the
  // auth check). Ignore the keystroke when the user is typing into an
  // editable element — they're entering a literal `?` into a field.
  useEffect(() => {
    if (!user) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (target.isContentEditable) return;
      }
      e.preventDefault();
      setCheatsheetOpen((prev) => !prev);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [user]);

  const toggleSidebarReveal = useCallback(() => {
    setSideRevealed((prev) => !prev);
  }, []);
  const closeSidebarReveal = useCallback(() => setSideRevealed(false), []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-[var(--color-ink-muted)]">
        {t('common.loading')}
      </div>
    );
  }
  if (!user) {
    navigate({ to: '/sign-in', replace: true });
    return null;
  }

  // /m and /m/* are the narrow-viewport companion routes — they ship
  // a purpose-built standalone layout and intentionally bypass the
  // desktop Shell chrome (sidebar, topbar, rail). Same bypass shape as
  // MobileSplash uses for the splash-overlay opt-out.
  if (path === '/m' || path.startsWith('/m/')) {
    return <Outlet />;
  }

  return (
    <RailContentProvider>
      <QuickCaptureProvider>
        <Shell
          sideCollapsed={!pinned}
          railCollapsed={railCollapsedReopener}
          noRail={railSuppressed}
          className={sideRevealed ? 'is-side-revealed' : undefined}
          onClick={sideRevealed ? closeSidebarReveal : undefined}
        >
          <Side id="portal-sidebar" onClick={(e) => e.stopPropagation()}>
            <SidebarNav />
          </Side>

          <PortalTopbar onMenuClick={toggleSidebarReveal} menuOpen={sideRevealed} />

          <Main aria-label="Main content">
            <Outlet />
          </Main>

          {railVisible && <PortalRail />}
          {railCollapsedReopener && <RailReopener />}

          <CommandPalette />
        </Shell>
        <ShortcutsCheatsheet open={cheatsheetOpen} onOpenChange={setCheatsheetOpen} />
        <QuickCaptureHotkey />
      </QuickCaptureProvider>
    </RailContentProvider>
  );
}

/**
 * Global ⌘N / Ctrl+N → open quick-capture. Mounted inside
 * <QuickCaptureProvider> so it can call useQuickCapture(). Mirrors the
 * existing `?` cheatsheet shortcut and the command palette's ⌘K wiring:
 *   - bypass when the user is typing into an editable element (input /
 *     textarea / select / contenteditable) so it doesn't steal "N" while
 *     they're filling a field
 *   - reuse the same metaKey-or-ctrlKey gate as command-palette so the
 *     keystroke works on macOS + Windows + Linux
 */
function QuickCaptureHotkey() {
  const { open } = useQuickCapture();
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      if (e.key.toLowerCase() !== 'n') return;
      // Don't intercept "N" while the user is typing — including
      // contenteditable surfaces (e.g. the notes TipTap editor).
      const target = e.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (target.isContentEditable) return;
      }
      e.preventDefault();
      open();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return null;
}
